import {INavGroup} from "../interfaces/INavGroup";
import {NavScope} from "../enum/NavScope";
import {PermissionTypes} from "../enum/PermissionTypes";

/**
 * ADMIN INFORMATION ARCHITECTURE — single source of truth (Release 1.19 IA pass).
 *
 * This declarative model replaces the two hand-written side-nav templates
 * (cross-game-admin-side-nav + game-admin-side-nav). Every admin screen belongs to
 * exactly one of the seven task domains below, plus an Account footer.
 *
 * Route strings are kept AS-THEY-ARE-TODAY so this config is truthful to the current
 * router; route renaming is deferred to a later stage.
 *
 * Scope model (see NavScope):
 *   - GameOnly   -> only when a game is selected; uses gameRoute (prefixed with gameId).
 *   - GlobalOnly -> only in all-games mode; uses globalRoute.
 *   - Both       -> visible in both; gameRoute when a game is active (falls back to the
 *                   flat globalRoute for screens with no per-game route), globalRoute otherwise.
 *
 * testId / gameTestId / globalTestId preserve the legacy nav's data-testids so the
 * Playwright suite keeps working after the two templates are retired.
 */
export const ADMIN_NAV: INavGroup[] = [
  {
    group: 'Draws & Games',
    icon: 'sports_esports',
    items: [
      {
        label: 'Current Draw',
        icon: 'casino',
        scope: NavScope.GameOnly,
        gameRoute: 'active-game-instances',
        gameTestId: 'current-draw-page-link',
      },
      {
        label: 'Active Draws',
        icon: 'scoreboard',
        scope: NavScope.GlobalOnly,
        globalRoute: 'cross-game-view-draw-landing',
        permission: PermissionTypes.ViewGameSummaryReport,
        globalTestId: 'view-active-draw-page-link',
      },
      {
        label: 'Draw Winner',
        icon: 'celebration',
        scope: NavScope.Both,
        gameRoute: 'draw-winner',
        globalRoute: 'cross-game-draw-winner',
        permission: PermissionTypes.DrawGameWinner,
        // In game scope, Draw Winner only applies to manually-drawn games (matches the
        // current game-admin nav's gameDrawnManually gate). No effect globally.
        gameCondition: 'drawnManually',
        testId: 'draw-winner-page-link',
      },
      {
        label: 'Game Settings',
        icon: 'settings',
        scope: NavScope.Both,
        gameRoute: 'manage-game',
        globalRoute: 'cross-game-game-management',
        permission: PermissionTypes.ViewGameSettings,
        gameTestId: 'sidenav-game-settings',
        globalTestId: 'cross-game-sidenav-game-settings',
      },
      // Game Rules and How To Play are not nav items (#1501). They are edited from Game Settings > General
      // Settings, alongside the rest of the game's content, and a second entry point here was both a
      // duplicate of that one and gated differently from it - the nav asked for ModifyGameSettings while the
      // buttons, and the endpoints that actually save, ask for EditGameTermsOfService / EditGameHowToPlay.
    ],
  },
  {
    group: 'Finance',
    icon: 'account_balance_wallet',
    items: [
      {
        // Distinct from the Finance group's wallet icon so it stands out in the list.
        label: 'Winner Payouts',
        icon: 'emoji_events',
        scope: NavScope.Both,
        gameRoute: 'game-admin-winner-payouts',
        globalRoute: 'cross-game-winner-payouts',
        permission: PermissionTypes.ViewWinnerPayouts,
        testId: 'view-winner-payout-page-link',
      },
      {
        label: 'Charity Payouts',
        icon: 'payments',
        scope: NavScope.Both,
        gameRoute: 'game-admin-charity-payouts',
        globalRoute: 'cross-game-charity-payouts',
        permission: PermissionTypes.ViewCharityPayouts,
        testId: 'view-charity-payout-page-link',
      },
      {
        // Per-game config with no cross-game equivalent — each organization's payout rules are its own.
        label: 'Payout Rules',
        icon: 'rule',
        scope: NavScope.GameOnly,
        gameRoute: 'game-admin-payout-rules',
        permission: PermissionTypes.ViewGamePayoutRules,
        gameTestId: 'payout-rules-page-link',
      },
      {
        // Game-scoped route keeps the game in context; per-game data filtering to follow.
        label: 'Failed Refunds',
        icon: 'money_off',
        scope: NavScope.Both,
        gameRoute: 'failed-refunds',
        globalRoute: 'failed-refunds',
        permission: PermissionTypes.ViewFailedRefunds,
        testId: 'failed-refunds-page-link',
      },
      {
        label: 'Payment Methods',
        icon: 'credit_card',
        scope: NavScope.Both,
        gameRoute: 'payment-methods',
        globalRoute: 'payment-methods',
        permission: PermissionTypes.ViewPlayerInfo,
        testId: 'payment-methods-page-link',
      },
      {
        // Fraud check, not a player list: the summary names no players, so it sits behind its own
        // permission rather than ViewPlayerInfo.
        label: 'Duplicate Card Fingerprints',
        icon: 'fingerprint',
        scope: NavScope.Both,
        gameRoute: 'duplicate-card-fingerprints',
        globalRoute: 'duplicate-card-fingerprints',
        permission: PermissionTypes.ViewDuplicateCardFingerprints,
        testId: 'duplicate-card-fingerprints-page-link',
      },
      {
        label: 'Region Tax Rates',
        icon: 'percent',
        scope: NavScope.GlobalOnly,
        globalRoute: 'manage-region-tax-rates',
        causableAdminOnly: true,
        globalTestId: 'manage-region-tax-rates-link',
      },
      {
        label: 'Causable Ledger',
        icon: 'request_quote',
        scope: NavScope.GlobalOnly,
        globalRoute: 'cross-game-causable-ledger',
        causableAdminOnly: true,
        globalTestId: 'causable-ledger-page-link',
      },
    ],
  },
  {
    group: 'Players',
    icon: 'group',
    items: [
      {
        label: 'Player Search',
        icon: 'person_search',
        scope: NavScope.Both,
        gameRoute: 'player-search',
        globalRoute: 'global-player-search',
        permission: PermissionTypes.ViewPlayerInfo,
        globalPermission: PermissionTypes.ViewGlobalPlayerSearch,
        gameTestId: 'player-search-page-link',
        globalTestId: 'global-player-search',
      },
      {
        // Game-scoped route keeps the game in context; per-game data filtering to follow.
        label: 'Email History',
        icon: 'mail',
        scope: NavScope.Both,
        gameRoute: 'email-history',
        globalRoute: 'email-history',
        permission: PermissionTypes.ViewPlayerInfo,
        testId: 'email-history',
      },
      {
        // Already filters by the active game; game-scoped route makes that explicit.
        label: 'Email Confirmations',
        icon: 'mark_email_read',
        scope: NavScope.Both,
        gameRoute: 'player-email-confirmations',
        globalRoute: 'player-email-confirmations',
        permission: PermissionTypes.ViewEmailConfirmations,
        testId: 'player-email-confirmations-page-link',
      },
      {
        label: 'Support Desk',
        icon: 'support_agent',
        scope: NavScope.Both,
        gameRoute: 'game-admin-support-desk-search',
        globalRoute: 'cross-game-support-desk-search',
        permission: PermissionTypes.ViewSupportRequests,
        badge: 'assignedSupportRequests',
        gameTestId: 'game-support-desk-page-link',
        globalTestId: 'causable-admin-support-desk-link',
      },
    ],
  },
  {
    group: 'Charities',
    icon: 'volunteer_activism',
    items: [
      {
        label: 'Charities',
        icon: 'volunteer_activism',
        scope: NavScope.Both,
        gameRoute: 'manage-charities',
        globalRoute: 'cross-game-charities',
        permission: PermissionTypes.ViewCharities,
        gameTestId: 'manage-charities-page-link',
        globalTestId: 'view-cross-game-charity-page-link',
      },
    ],
  },
  {
    // Group uses a general comms icon; Broadcast keeps the distinct megaphone below.
    group: 'Comms & Content',
    icon: 'forum',
    items: [
      {
        label: 'Broadcast Notifications',
        icon: 'campaign',
        scope: NavScope.Both,
        gameRoute: 'manage-broadcast-notifications',
        globalRoute: 'manage-broadcast-notifications',
        permission: PermissionTypes.ViewBroadcastNotifications,
        testId: 'manage-broadcast-notifications-page-link',
      },
      {
        label: 'Notification Preferences',
        icon: 'notifications_active',
        scope: NavScope.Both,
        gameRoute: 'game-admin-notification-preferences',
        globalRoute: 'cross-game-notification-preferences',
        testId: 'notification-preferences-page-link',
      },
      {
        label: 'FAQ',
        icon: 'quiz',
        scope: NavScope.Both,
        gameRoute: 'frequently-asked-questions',
        globalRoute: 'frequently-asked-questions',
        testId: 'faq-page-link',
      },
    ],
  },
  {
    group: 'Reporting & Audit',
    icon: 'assessment',
    items: [
      {
        label: 'Reports',
        icon: 'request_page',
        scope: NavScope.Both,
        gameRoute: 'game-admin-reporting',
        globalRoute: 'cross-game-admin-reporting',
        permission: PermissionTypes.ViewReports,
        gameTestId: 'reports-page-link',
        globalTestId: 'view-cross-game-reporting-page-link',
      },
      {
        label: 'Report Builder',
        icon: 'query_stats',
        scope: NavScope.Both,
        gameRoute: 'report-builder',
        globalRoute: 'cross-game-report-builder',
        permission: PermissionTypes.ViewDynamicReport,
        gameTestId: 'report-builder-page-link',
        globalTestId: 'cross-game-report-builder-page-link',
      },
      {
        label: 'Auditing',
        icon: 'history',
        scope: NavScope.Both,
        gameRoute: 'auditing',
        globalRoute: 'auditing',
        causableAdminOnly: true,
        testId: 'auditing-page-link',
      },
      {
        label: 'Error Debugging',
        icon: 'bug_report',
        scope: NavScope.GlobalOnly,
        globalRoute: 'error-debugging',
        causableAdminOnly: true,
        globalTestId: 'error-debugging-page-link',
      },
      {
        label: 'Game Instance Emails',
        icon: 'outgoing_mail',
        scope: NavScope.GlobalOnly,
        globalRoute: 'view-player-emails',
        causableAdminOnly: true,
        globalTestId: 'game-instance-player-emails-link',
      },
      {
        label: 'Sent Emails',
        icon: 'mark_email_read',
        scope: NavScope.GlobalOnly,
        globalRoute: 'sent-emails',
        causableAdminOnly: true,
        globalTestId: 'sent-emails-page-link',
      },
    ],
  },
  {
    group: 'System',
    icon: 'engineering',
    items: [
      {
        label: 'New Game',
        icon: 'add_circle',
        // Creating a game belongs to no game, so it is offered whichever scope the admin is in.
        scope: NavScope.Both,
        globalRoute: 'new-game',
        causableAdminOnly: true,
        testId: 'new-game-page-link',
      },
      {
        label: 'Admin Users',
        icon: 'manage_accounts',
        scope: NavScope.Both,
        gameRoute: 'manage-game-admin-users',
        globalRoute: 'manage-cross-game-users',
        permission: PermissionTypes.ViewAdminUsers,
        testId: 'manage-users-page-link',
      },
      {
        label: 'Global Permissions',
        icon: 'admin_panel_settings',
        scope: NavScope.GlobalOnly,
        globalRoute: 'manage-global-permissions',
        causableAdminOnly: true,
        globalTestId: 'manage-permissions-page-link',
      },
      {
        label: 'Error Categories',
        icon: 'error_outline',
        scope: NavScope.GlobalOnly,
        globalRoute: 'error-category-management',
        permission: PermissionTypes.ViewSystemErrorMessages,
        globalTestId: 'error-category-management-page-link',
      },
      {
        label: 'Profanity Filter',
        icon: 'filter_alt',
        scope: NavScope.GlobalOnly,
        globalRoute: 'profanity-filter-manager',
        causableAdminOnly: true,
        globalTestId: 'profanity-filter-manager-page-link',
      },
      {
        label: 'Log Level',
        icon: 'tune',
        scope: NavScope.GlobalOnly,
        globalRoute: 'log-level-switch',
        causableAdminOnly: true,
        globalTestId: 'logging-level-switch-link',
      },
      {
        label: 'File Integrity',
        icon: 'verified_user',
        scope: NavScope.GlobalOnly,
        globalRoute: 'file-integrity-dashboard',
        causableAdminOnly: true,
        globalTestId: 'file-integrity-dashboard-link',
      },
      {
        label: 'Signal-R Connections',
        icon: 'cable',
        scope: NavScope.GlobalOnly,
        globalRoute: 'signal-r-connections',
        causableAdminOnly: true,
        globalTestId: 'signal-r-page-link',
      },
      {
        label: 'Testing',
        icon: 'science',
        scope: NavScope.GlobalOnly,
        globalRoute: 'testing-page',
        causableAdminOnly: true,
        globalTestId: 'testing-page-link',
      },
      {
        label: 'Hangfire Dashboard',
        icon: 'dashboard',
        scope: NavScope.GlobalOnly,
        causableAdminOnly: true,
        externalLink: 'hangfire',
        globalTestId: 'hangfire-dashboard-link',
      },
    ],
  },
  {
    group: 'Account',
    icon: 'account_circle',
    isFooter: true,
    items: [
      {
        label: 'Change Email',
        icon: 'email',
        scope: NavScope.Both,
        gameRoute: 'update-email-address',
        globalRoute: 'update-email-address',
        testId: 'navigate-to-change-email-page',
      },
      {
        label: 'Change Password',
        icon: 'password',
        scope: NavScope.Both,
        gameRoute: 'update-password',
        globalRoute: 'update-password',
        testId: 'navigate-to-change-password-page',
      },
      {
        label: 'Admin Terms',
        icon: 'description',
        scope: NavScope.GlobalOnly,
        globalRoute: 'privacy-policy',
        globalTestId: 'admin-terms-link',
      },
    ],
  },
];
